/* eslint-disable react/prop-types */
import { Box, Button, Grid, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import React, { useMemo, useState, useCallback, useEffect } from "react";
import ReactApexCharts from "react-apexcharts";
import ApexCharts from "apexcharts";
import { seriesData } from "../constants/demoData";
import logo from "../assets/Pulsar_Logo_Color.svg";
import { DataGrid } from "@mui/x-data-grid";
import { DateTime } from "luxon";

const actions = [
  {
    label: "1M",
    value: "one_month",
  },
  {
    label: "6M",
    value: "six_months",
  },
  {
    label: "1Y",
    value: "one_year",
  },
  {
    label: "YTD",
    value: "ytd",
  },
  {
    label: "ALL",
    value: "all",
  },
];

const LineChartSmall = ({ stats }) => {
  const classes = useStyles()();
  const [activeTimeline, setActiveTimeline] = useState("one_year");
  const [isChart, setIsChart] = useState(true);
  const [series, setSeries] = useState([...seriesData]);
  const [isStakeHistory, setIsStakeHistory] = useState(false);
  const stakeAmountMemo = useMemo(() => {
    if (stats?.data?.stake_pool?.total_staked_amount) {
      let parts = stats?.data?.stake_pool?.total_staked_amount.toFixed();
      parts = parts.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return parts;
    }
    return "0.0";
  }, [stats?.data?.stake_pool?.total_staked_amount]);

  // table data
  const columns = [
    { field: "date", headerName: "Date", minWidth: 200, flex: 1 },
    {
      field: isStakeHistory ? "stakes" : "rewards",
      headerName: isStakeHistory ? "Stakes" : "Rewards",
      minWidth: 150,
      flex: 1,
    },
  ];
  // stake rows
  const stakeRows = useMemo(() => {
    let rowData = stats?.data?.user?.stake_history;
    if (rowData?.length > 0) {
      rowData = rowData.map((item, index) => {
        const date = new Date(item.time_of_deposit);
        const day = ("0" + date.getDate()).slice(-2);
        const month = ("0" + (date.getMonth() + 1)).slice(-2);
        const year = date.getFullYear();
        let hours = date.getHours();
        let minutes = date.getMinutes();
        let ampm = hours >= 12 ? "pm" : "am";
        hours = hours % 12;
        hours = hours ? hours : 12; // the hour '0' should be '12'
        minutes = minutes < 10 ? "0" + minutes : minutes;
        let strTime = hours + ":" + minutes + " " + ampm;
        const time = `${day}/${month}/${year}, ${strTime}`;
        return {
          id: index,
          date: time,
          stakes: item?.amount_staked?.toFixed(5),
        };
      });
    } else rowData = [];
    return rowData.reverse();
  }, [stats?.data?.user?.stake_history]);

  // reward rows
  const rewardRows = useMemo(() => {
    let rowData = stats?.data?.user?.stake_reward_history;
    if (rowData?.length > 0) {
      rowData = rowData.map((item, index) => {
        const date = new Date(item.time_of_credit);
        const day = ("0" + date.getDate()).slice(-2);
        const month = ("0" + (date.getMonth() + 1)).slice(-2);
        const year = date.getFullYear();
        let hours = date.getHours();
        let minutes = date.getMinutes();
        let ampm = hours >= 12 ? "pm" : "am";
        hours = hours % 12;
        hours = hours ? hours : 12; // the hour '0' should be '12'
        minutes = minutes < 10 ? "0" + minutes : minutes;
        let strTime = hours + ":" + minutes + " " + ampm;
        const time = `${day}/${month}/${year}, ${strTime}`;
        return {
          id: index,
          date: time,
          rewards: item?.stake_reward?.toFixed(9),
        };
      });
    } else rowData = [];
    return rowData.reverse();
  }, [stats?.data?.user?.stake_reward_history]);

  const seriesStakeMemo = useMemo(() => {
    let seriesData = stats?.data?.user?.stake_history;
    if (seriesData?.length > 0) {
      // time line logic - sort in ascending order (oldest first)
      const sortedData = [...seriesData]
        .sort(
          (a, b) =>
            new Date(a.time_of_deposit).getTime() -
            new Date(b.time_of_deposit).getTime(),
        )
        .map((item) => {
          return [
            new Date(item?.time_of_deposit).getTime(),
            item?.amount_staked?.toFixed(9),
          ];
        });
      seriesData = [{ data: sortedData }];
    } else seriesData = []; // series;
    return seriesData;
  }, [stats, series]);
  const seriesRewardMemo = useMemo(() => {
    let seriesData = stats?.data?.user?.stake_reward_history;
    if (seriesData?.length > 0) {
      // time line logic - sort in ascending order (oldest first)
      const sortedData = [...seriesData]
        .sort(
          (a, b) =>
            new Date(a.time_of_credit).getTime() -
            new Date(b.time_of_credit).getTime(),
        )
        .map((item) => {
          return [
            new Date(item?.time_of_credit).getTime(),
            item?.stake_reward?.toFixed(9),
          ];
        });
      seriesData = [{ data: sortedData }];
    } else seriesData = []; // series;
    return seriesData;
  }, [stats, series]);

  // chart data
  const options = {
    chart: {
      id: "area-datetime",
      type: "area",
      height: 350,
      zoom: {
        autoScaleYaxis: true,
      },
    },
    colors: ["#0097ce"],
    annotations: {
      yaxis: [
        {
          y: 30,
          borderColor: "#999",
          label: {
            show: true,
            text: "Time",
            style: {
              color: "#fff",
              background: "#68beff",
            },
          },
        },
      ],
      xaxis: [
        {
          x: new Date("1 Jun 2012").getTime(),
          borderColor: "#999",
          yAxisIndex: 0,
          label: {
            show: true,
            text: isStakeHistory ? "Stakes" : "Rewards",
            style: {
              color: "#fff",
              background: "#0097ce",
            },
          },
        },
      ],
    },
    dataLabels: {
      enabled: false,
    },
    markers: {
      size: 0,
      style: "hollow",
    },
    xaxis: {
      type: "datetime",
      min: new Date("1 Nov 2022").getTime(),
      tickAmount: 6,
      labels: {
        style: {
          colors: "#186ABD",
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: "#186ABD",
        },
      },
    },
    tooltip: {
      style: {
        color: "#000 !important",
      },
      x: {
        format: "dd MMM yyyy",
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.9,
        stops: [0, 100],
      },
    },
  };

  const updateTimeline = useCallback(
    (timeline) => {
      setActiveTimeline(timeline.value);

      let seriesStakeData = stats?.data?.user?.stake_history;
      let seriesRewardData = stats?.data?.user?.stake_reward_history;
      let dataTimeline, start, end;
      if (isStakeHistory) {
        if (seriesStakeData?.length > 0) {
          // time line logic
          start = DateTime.fromISO(seriesStakeData[0]?.time_of_deposit);
          end = DateTime.fromISO(
            seriesStakeData[seriesStakeData?.length - 1]?.time_of_deposit,
          );
          dataTimeline = true;
        }
      } else {
        if (seriesRewardData?.length > 0) {
          // time line logic
          start = DateTime.fromISO(seriesRewardData[0]?.time_of_credit);
          end = DateTime.fromISO(
            seriesRewardData[seriesRewardData?.length - 1]?.time_of_credit,
          );
          dataTimeline = true;
        }
      }

      setTimeout(() => {
        switch (timeline.value) {
          case "one_month":
            dataTimeline
              ? ApexCharts.exec(
                  "area-datetime",
                  "zoomX",
                  new Date(start.toISO()).getTime(),
                  new Date(start.plus({ months: 1 }).toISO()).getTime(),
                )
              : ApexCharts.exec(
                  "area-datetime",
                  "zoomX",
                  new Date("28 Jan 2013").getTime(),
                  new Date("27 Feb 2013").getTime(),
                );
            break;
          case "six_months":
            dataTimeline
              ? ApexCharts.exec(
                  "area-datetime",
                  "zoomX",
                  new Date(start.toISO()).getTime(),
                  new Date(start.plus({ months: 6 }).toISO()).getTime(),
                )
              : ApexCharts.exec(
                  "area-datetime",
                  "zoomX",
                  new Date("27 Sep 2012").getTime(),
                  new Date("27 Feb 2013").getTime(),
                );
            break;
          case "one_year":
            dataTimeline
              ? ApexCharts.exec(
                  "area-datetime",
                  "zoomX",
                  new Date(start.toISO()).getTime(),
                  new Date(start.plus({ months: 12 }).toISO()).getTime(),
                )
              : ApexCharts.exec(
                  "area-datetime",
                  "zoomX",
                  new Date("27 Feb 2012").getTime(),
                  new Date("27 Feb 2013").getTime(),
                );
            break;
          case "ytd":
            dataTimeline
              ? ApexCharts.exec(
                  "area-datetime",
                  "zoomX",
                  new Date(end.plus({ month: -12 })).getTime(),
                  new Date(end.toISO()).getTime(),
                )
              : ApexCharts.exec(
                  "area-datetime",
                  "zoomX",
                  new Date("01 Jan 2013").getTime(),
                  new Date("27 Feb 2013").getTime(),
                );
            break;
          case "all":
            dataTimeline
              ? ApexCharts.exec(
                  "area-datetime",
                  "zoomX",
                  new Date(start).getTime(),
                  new Date(end).getTime(),
                )
              : ApexCharts.exec(
                  "area-datetime",
                  "zoomX",
                  new Date("23 Jan 2012").getTime(),
                  new Date("27 Feb 2013").getTime(),
                );
            break;
          default:
            break;
        }
      }, 500);
    },
    [isStakeHistory, stats, activeTimeline],
  );
  useEffect(() => {
    let seriesStakeData = stats?.data?.user?.stake_history;
    let seriesRewardData = stats?.data?.user?.stake_reward_history;
    let dataTimeline, start, end;
    if (isStakeHistory) {
      if (seriesStakeData?.length > 0) {
        // time line logic
        start = DateTime.fromISO(seriesStakeData[0]?.time_of_deposit);
        end = DateTime.fromISO(
          seriesStakeData[seriesStakeData?.length - 1]?.time_of_deposit,
        );
        dataTimeline = true;
      }
    } else {
      if (seriesRewardData?.length > 0) {
        // time line logic
        start = DateTime.fromISO(seriesRewardData[0]?.time_of_credit);
        end = DateTime.fromISO(
          seriesRewardData[seriesRewardData?.length - 1]?.time_of_credit,
        );
        dataTimeline = true;
      }
    }

    setTimeout(
      () =>
        dataTimeline
          ? ApexCharts.exec(
              "area-datetime",
              "zoomX",
              new Date(start.toISO()).getTime(),
              new Date(start.plus({ months: 12 }).toISO()).getTime(),
            )
          : ApexCharts.exec(
              "area-datetime",
              "zoomX",
              new Date("27 Feb 2012").getTime(),
              new Date("27 Feb 2013").getTime(),
            ),
      500,
    );
  }, []);
  return (
    <>
      <div className={classes.chartWrap}>
        <Grid container direction={"row"} justifyContent="space-between">
          <div className={classes.subtitle}>
            <div>
              <img width="48" height="48" src={logo} alt="pulsar logo" />
              <span>Total Locked Pulsars</span>
            </div>
            <p>{stats ? stakeAmountMemo : "0.0"} PLSR</p>
          </div>
          <div className={classes.timeToggler}>
            {actions.map((item) => (
              <Button
                sx={{
                  backgroundColor:
                    activeTimeline === item.value ? "#fff !important" : "",
                }}
                onClick={() => updateTimeline(item)}
                key={item.value}
                type="button"
              >
                {item.label}
              </Button>
            ))}
          </div>
          {isChart ? (
            <ReactApexCharts
              className={classes.chart}
              options={options}
              series={isStakeHistory ? seriesStakeMemo : seriesRewardMemo}
              type="area"
              height={450}
            />
          ) : (
            <Box className={classes.table}>
              <DataGrid
                rows={isStakeHistory ? stakeRows : rewardRows}
                columns={columns}
                checkboxSelection
                rowsPerPageOptions={[25]}
                disableSelectionOnClick
                experimentalFeatures={{ newEditingApi: true }}
              />
            </Box>
          )}
          <div className={classes.statsToggle}>
            <Button
              type="button"
              onClick={() => setIsStakeHistory((prev) => !prev)}
            >
              {isStakeHistory ? "Rewards" : "Stakes"}
            </Button>
            <Button type="button" onClick={() => setIsChart((prev) => !prev)}>
              {isChart ? "Table" : "Chart"}
            </Button>
          </div>
        </Grid>
      </div>
    </>
  );
};

export default LineChartSmall;

function useStyles() {
  return makeStyles((theme) => ({
    chartWrap: {
      border: `none`,
      borderRadius: "0.4375rem",
      padding: "1rem clamp(1rem, 5vw + 1rem, 2rem)",
      backgroundColor: theme.staking.chartWrap.background,
      flex: 1.95,
      width: "100%",
      "@media screen and (max-width : 30.5625rem)": {
        padding: "0.25rem !important",
      },
      "&  *": {
        fontFamily: "AvenirNext-400 !important",
      },
      "& > div ": {
        minWidth: "100%",
        minHeight: "100%",
      },
    },
    subtitle: {
      maxHeight: "6.25rem",
      "@media screen and (max-width : 30.5625rem)": {
        padding: "0.5rem !important",
      },
      "& > div > img": {
        marginBottom: "-0.7rem",
        marginLeft: "-0.7rem",
      },
      "& > div > span": {
        color: theme.staking.p,
        fontFamily: "AvenirNext-400 !important",
        fontWeight: "bold",
      },
      "& > p ": {
        color: theme.staking.p,
        fontFamily: "AvenirNextSemiBold !important",
        fontWeight: "bolder",
        fontSize: "clamp(1.2rem, 4vw + 0.5rem, 1.3rem)",
      },
    },
    timeToggler: {
      display: "flex",
      flexFlow: "row nowrap",
      backgroundColor: theme.staking.timeToggler.background,
      alignItems: "center",
      maxHeight: "45px",
      padding: "0px 4px !important",
      marginBottom: "3rem",
      borderRadius: "0.3125rem",
      "& > button": {
        color: `${theme.staking.p} !important`,
        padding: "8px 0px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        lineHeight: "1",
        cursor: "pointer",
        boxSizing: "border-box",
      },
      "@media screen and (max-width : 25rem)": {
        "& > button": {
          minWidth: "clamp(40px, 8vw + 1.5rem, 64px)",
        },
      },
      fontFamily: "AvenirNext-400 !important",
      "@media screen and (max-width : 30.5625rem)": {
        marginLeft: "0.5rem !important",
      },
    },
    chart: {
      width: "calc(100% + 1rem)",
      marginLeft: "-1rem",
      "& > .apexcharts-xaxis": {
        color: theme.palette.mode === "dark" ? "#fff" : "#000",
      },
    },
    statsToggle: {
      width: "100%",
      display: "flex",
      justifyContent: "space-between",
      maxHeight: "3.25rem",
      "& > button": {
        border: "1px solid #0097ce",
        color: theme.palette.mode === "dark" ? "" : "#0097ce",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          scale: "1.05",
          transform: "scale(1.05)",
          transition: "all 0.2s ease-in-out",
        },
      },
    },
    table: {
      backgroundColor: theme.staking.card.background,
      minHeight: "27.0625rem",
      width: "100%",
      marginBottom: "1.5rem",
    },
  }));
}
